/**
 * "Request this item" — point 14's ITEM half, beside the state that reveals the need.
 *
 * A line the catalogue cannot match already offers "click to match". That is the right escape when
 * the item IS in the master under another description; it is the wrong one when the master simply
 * does not have it, and until now the purchaser's only options were to keep searching or to leave
 * the line unmatched.
 *
 * ---- It files a REQUEST, and says so at every step ----
 *
 * `items.item_creation_request` creates an `Item Creation Request` document for someone to
 * approve. The catalogue is UNCHANGED, so the line still cannot be matched afterwards. Every
 * string here is careful about that — the same rule `RequestUomLink` follows, for the same reason.
 *
 * ---- Why a dialog rather than a one-click link ----
 *
 * The UOM half needs only the unit, which the line already carries, so it is a link. This needs
 * `item_group` and `item_uom` as well, and the endpoint 500s without either. Neither can be
 * inferred: an unmatched line has no item and therefore no group, and the line's own unit is
 * frequently the `Nos` placeholder rather than anything the customer said.
 */

import { useState } from 'react'
import { Modal } from '../../components/Modal'
import { Button } from '../../components/ui'
import { useToast } from '../../components/Toast'
import { useRequestItem, validateItemRequest } from '../../lib/api/itemRequest'
import { useItemGroupSuppliers } from '../../lib/api/useItemGroupSuppliers'
import { useUomMaster } from '../../lib/api/useUoms'

export function RequestItemLink({
  description,
  uom,
  enquiryId,
  rowId,
  existingRequest,
  onRequested,
  disabled,
  as = 'link',
}: {
  /** The enquiry the request is raised from, and the line's serial — required by the server
   *  since 2026-09-19 (`reference_name` / `row_id`); the serial is blank from the toolbar. */
  enquiryId: string
  rowId?: string
  /**
   * The request already on the line (`Enquiry Item.item_creation_req_ref`), so a reload still
   * shows it as requested; and the write that records a new one there, right after the server
   * answers (2026-09-19). Both absent from the toolbar, where there is no line.
   */
  existingRequest?: string | null
  onRequested?: (requestName: string) => void
  /** The customer's own words for the line — the description being requested. Empty from the
   *  toolbar, where there is no line and the purchaser types it. */
  description: string
  /** The line's current unit, offered as the default. Empty from the toolbar. */
  uom: string
  disabled?: boolean
  /**
   * `link` sits under a line's unmatched badge; `button` sits in the page toolbar, where the
   * action is offered unconditionally because the matcher answers a 3%-confidence candidate
   * rather than nothing, so a line is rarely "unmatched" even when the item is genuinely absent.
   */
  as?: 'link' | 'button'
}) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [requested, setRequested] = useState<string | null>(existingRequest ?? null)

  const groups = useItemGroupSuppliers()
  const uoms = useUomMaster()

  const [name, setName] = useState(description)
  const [group, setGroup] = useState('')
  const [unit, setUnit] = useState(uom)

  const request = useRequestItem({
    onSuccess: (docname, itemName) => {
      setRequested(docname)
      setOpen(false)
      // Recorded on the line — `item_creation_req_ref` — so it survives a reload and the
      // approver can find the enquiry line the request came from.
      onRequested?.(docname)
      // "Requested", never "created" — see the header.
      toast(`Requested “${itemName}” — an item creation request was filed for approval.`)
    },
  })

  if (requested) {
    return as === 'button' ? (
      <Button disabled title={`Requested ${requested} — awaiting approval`}>
        ✓ item requested
      </Button>
    ) : (
      <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 3 }}>
        · requested <span className="mono">{requested}</span> — awaiting approval
      </div>
    )
  }

  const problem = validateItemRequest({ item_name: name, item_group: group, item_uom: unit })

  return (
    <>
      {as === 'button' ? (
        <Button disabled={disabled} onClick={() => setOpen(true)}>
          ✚ Request a new item
        </Button>
      ) : (
        <div style={{ marginTop: 3 }}>
          <span
            className="linkish"
            style={{ fontSize: 11, ...(disabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}) }}
            onClick={() => !disabled && setOpen(true)}
          >
            not in the catalogue at all? request it →
          </span>
        </div>
      )}

      {open ? (
        <Modal title="Request a new item" onClose={() => setOpen(false)}>
          <div className="pad">
            <div className="note" style={{ marginBottom: 12 }}>
              This files a request for someone to approve. It does <b>not</b> create the item, so
              this line still cannot be matched to it until the request is approved.
            </div>

            <label className="ef">
              <span>Description</span>
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </label>

            <label className="ef" style={{ marginTop: 10 }}>
              <span>Item group</span>
              {/* From the groups already loaded for supplier mapping — no extra read, and it is
                  the same vocabulary the catalogue itself uses. */}
              <select value={group} onChange={(e) => setGroup(e.target.value)}>
                <option value="">Choose a group…</option>
                {[...(groups.data?.keys() ?? [])].sort().map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </label>

            <label className="ef" style={{ marginTop: 10 }}>
              <span>Unit</span>
              <select value={unit} onChange={(e) => setUnit(e.target.value)}>
                <option value="">Choose a unit…</option>
                {(uoms.data ?? []).map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </label>

            {problem ? (
              <div style={{ color: 'var(--warn)', fontSize: 12, marginTop: 10 }}>{problem}</div>
            ) : null}
          </div>

          <div className="pad" style={{ borderTop: '1px solid var(--line)', display: 'flex', gap: 8 }}>
            <span style={{ flex: 1 }} />
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!!problem || request.isPending}
              onClick={() =>
                request.mutate({ item_name: name, item_group: group, item_uom: unit, enquiryId, rowId })
              }
            >
              {request.isPending ? 'Filing…' : 'Request this item'}
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  )
}
